import React, { useEffect, useMemo, useState } from "react";
import { Button, Drawer, Popover } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import PickerList, { PickerGroup } from "./PickerList";
import MonthGrid from "./MonthGrid";
import SheetHeaderClose from "./SheetHeaderClose";
import { CalendarIcon, PinIcon } from "../search-icons";
import { monthFormat } from "../../../../utils/antd.props";
import type { Country } from "../../../../interfaces/country.interface";
import type { GroundFilterState } from "../../../../utils/groundListingParams";

interface Props {
  isMobile: boolean;
  filters: GroundFilterState;
  countries: Country[];
  onSearch: (next: GroundFilterState) => void;
}

type OpenField = "keyword" | "month" | null;

// Keyword + travel month, as per the Figma search pill. Ground tours have no
// departure city or brand field, so this is a two-field cut of the series-tour
// search bar rather than a reuse of it.
export default function GroundSearchBar({ isMobile, filters, countries, onSearch }: Props) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<GroundFilterState>(filters);
  const [open, setOpen] = useState<OpenField>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [keywordText, setKeywordText] = useState(filters.keyword);
  const [year, setYear] = useState(filters.month ? dayjs(filters.month).year() : dayjs().year());

  useEffect(() => {
    setDraft(filters);
    setKeywordText(filters.keyword);
    if (filters.month) setYear(dayjs(filters.month).year());
  }, [filters]);

  // The keyword field searches free text (title, code, city, country), so the
  // suggestions are names rather than location ids - picking a location id is
  // what the Destination modal is for.
  const destinationGroups = useMemo<PickerGroup[]>(() => {
    const countryOptions = countries.map((country) => ({ value: country.name, label: country.name }));
    const countryNames = new Set(countries.map((country) => country.name.toLowerCase()));
    const cityOptions = countries
      .flatMap((country) => country.cities || [])
      .filter((city) => !countryNames.has(city.name.toLowerCase()))
      .map((city) => ({ value: city.name, label: city.name }));

    return [
      { heading: "Destinations", options: countryOptions },
      { heading: "Cities", options: cityOptions },
    ].filter((group) => group.options.length > 0);
  }, [countries]);

  const monthLabel = draft.month ? dayjs(draft.month).format(monthFormat) : "";

  if (isMobile) {
    // Ground tours have no mobile frame in the design yet, so the pill opens a
    // sheet built from the same destination list and month grid as the desktop
    // dropdowns rather than inventing a new layout.
    return (
      <div className="v2-listing__searchbar v2-listing__searchbar--mobile">
        <button type="button" className="v2-listing__back" onClick={() => navigate(-1)} aria-label="Back">
          <img src="/iceholidays-assets/images/icons/chevron-left-thin.svg" alt="" />
        </button>

        <button type="button" className="v2-listing__pill" onClick={() => setSheetOpen(true)}>
          <img className="v2-listing__pill-icon" src="/iceholidays-assets/images/icons/search-red.svg" alt="" />
          <span className="v2-listing__pill-text">
            <span className="v2-listing__pill-primary">{filters.keyword || "Anywhere"}</span>
            <span className="v2-listing__pill-secondary">{monthLabel || "Any date"}</span>
          </span>
        </button>

        <Drawer
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          placement="bottom"
          height="90%"
          title="Search"
          closable={false}
          extra={<SheetHeaderClose onClose={() => setSheetOpen(false)} />}
          rootClassName="v2-sheet v2-sheet--ground-search"
          footer={
            <Button
              type="primary"
              className="v2-sheet__submit"
              block
              onClick={() => {
                onSearch(draft);
                setSheetOpen(false);
              }}
            >
              Search Holidays
            </Button>
          }
        >
          <p className="v2-sheet__lede">Where would you like to go?</p>
          <PickerList
            groups={destinationGroups}
            rowIcon={<PinIcon size={16} />}
            value={draft.keyword}
            query={keywordText}
            onSelect={(option) => {
              setDraft({ ...draft, keyword: option.value });
              setKeywordText(option.label);
            }}
          />

          <p className="v2-sheet__lede">Travel date</p>
          <MonthGrid
            year={year}
            onYearChange={setYear}
            month={draft.month && dayjs(draft.month).year() === year ? dayjs(draft.month).month() : null}
            onMonthChange={(month) =>
              setDraft({
                ...draft,
                month: month === null ? null : dayjs().year(year).month(month).format("YYYY-MM-01"),
              })
            }
          />
        </Drawer>
      </div>
    );
  }

  const dropdown = (field: OpenField, content: React.ReactNode, children: React.ReactNode) => (
    <Popover
      open={open === field}
      onOpenChange={(next) => setOpen(next ? field : null)}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      content={content}
      rootClassName="v2-field-dropdown"
    >
      {children}
    </Popover>
  );

  return (
    <div className="v2-listing__searchbar">
      <div className="v2-search-fields">
        {dropdown(
          "keyword",
          <div className="v2-field-dropdown__list">
            <PickerList
              groups={destinationGroups}
              rowIcon={<PinIcon size={16} />}
              value={draft.keyword}
              query={keywordText}
              onSelect={(option) => {
                setDraft({ ...draft, keyword: option.value });
                setKeywordText(option.label);
                setOpen(null);
              }}
            />
          </div>,
          <div className="v2-search-field">
            <PinIcon size={20} />
            <input
              className="v2-search-field__input"
              value={keywordText}
              placeholder="Where would you like to go?"
              onChange={(event) => {
                setKeywordText(event.target.value);
                setDraft({ ...draft, keyword: event.target.value });
                setOpen("keyword");
              }}
            />
          </div>
        )}

        {dropdown(
          "month",
          <div className="v2-field-dropdown__months">
            <MonthGrid
              year={year}
              onYearChange={setYear}
              month={draft.month && dayjs(draft.month).year() === year ? dayjs(draft.month).month() : null}
              onMonthChange={(month) => {
                setDraft({
                  ...draft,
                  month: month === null ? null : dayjs().year(year).month(month).format("YYYY-MM-01"),
                });
                if (month !== null) setOpen(null);
              }}
            />
          </div>,
          <button type="button" className="v2-search-field">
            <CalendarIcon size={20} />
            <span className={`v2-search-field__value ${draft.month ? "" : "is-empty"}`}>
              {monthLabel || "Travel Date"}
            </span>
          </button>
        )}
      </div>

      <Button type="primary" className="v2-listing__search-btn" onClick={() => onSearch(draft)}>
        Search Holidays
      </Button>
    </div>
  );
}
